import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { api, apiError } from "../lib/api";
import Layout from "../components/Layout";
import ResourceContent from "../components/ResourceContent";
import { toast } from "sonner";
import { Loader2, ScanLine, Upload, ImagePlus, X } from "lucide-react";

const GRADES = ["1. клас","2. клас","3. клас","4. клас","5. клас","6. клас","7. клас","8. клас","9. клас","10. клас","11. клас","12. клас"];
const SUBJECTS = ["","Български език и литература","Математика","Околен свят","Човекът и природата","История и цивилизации","География и икономика","Биология","Физика","Химия","Английски език"];

function Dropzone({ label, file, onFile, testid }) {
  const ref = useRef();
  return (
    <div>
      <label className="text-sm font-bold text-slate-700 mb-1.5 block">{label}</label>
      <div onClick={() => ref.current?.click()} data-testid={testid}
        className="border-2 border-dashed border-slate-200 rounded-2xl p-4 text-center cursor-pointer hover:border-sky-300 hover:bg-sky-50/50 transition-colors">
        <input ref={ref} type="file" accept="image/*" className="hidden"
          onChange={(e) => onFile(e.target.files?.[0] || null)} />
        {file ? (
          <div className="relative">
            <img src={URL.createObjectURL(file)} alt="" className="max-h-44 mx-auto rounded-xl object-contain" />
            <button onClick={(e) => { e.stopPropagation(); onFile(null); }} className="absolute top-1 right-1 bg-white rounded-full p-1 shadow"><X size={14} /></button>
            <p className="text-xs text-slate-400 mt-2 truncate">{file.name}</p>
          </div>
        ) : (
          <div className="py-6 text-slate-400">
            <ImagePlus size={28} className="mx-auto mb-2 text-sky-400" />
            <p className="text-sm font-medium">Кликни за снимка на теста</p>
            <p className="text-xs">JPG / PNG</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Grading() {
  const [testImg, setTestImg] = useState(null);
  const [keyImg, setKeyImg] = useState(null);
  const [answerText, setAnswerText] = useState("");
  const [subject, setSubject] = useState("");
  const [grade, setGrade] = useState("5. клас");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!testImg) return toast.error("Качете снимка на теста");
    setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("test_image", testImg);
      if (keyImg) fd.append("answer_key", keyImg);
      fd.append("answer_text", answerText);
      fd.append("subject", subject);
      fd.append("grade", grade);
      fd.append("model", "gpt-5.4");
      const { data } = await api.post("/grade", fd);
      setResult(data);
      toast.success("Тестът е проверен!");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="max-w-5xl">
        <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900">✅ Проверка на тестове</h1>
        <p className="text-slate-500 mt-2">Снимай попълнения тест на ученика — AI го проверява и оценява вместо теб.</p>

        <div className="bg-white border border-slate-100 rounded-3xl p-7 mt-6 shadow-sm space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <Dropzone label="Снимка на попълнения тест *" file={testImg} onFile={setTestImg} testid="test-image-drop" />
            <Dropzone label="Отговорен ключ (по избор)" file={keyImg} onFile={setKeyImg} testid="key-image-drop" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-bold text-slate-700 mb-1.5 block">Учебен предмет</label>
              <select data-testid="grading-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-sky-300 outline-none bg-white">
                {SUBJECTS.map((s) => <option key={s} value={s}>{s || "Не е посочен"}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-bold text-slate-700 mb-1.5 block">Клас</label>
              <select data-testid="grading-grade" value={grade} onChange={(e) => setGrade(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-sky-300 outline-none bg-white">
                {GRADES.map((g) => <option key={g}>{g}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm font-bold text-slate-700 mb-1.5 block">Верни отговори / критерии (по избор)</label>
            <textarea data-testid="answer-text" rows="2" value={answerText} placeholder="напр. 1) 12  2) 5  3) 12  4) 5 — или опиши критериите за оценяване"
              onChange={(e) => setAnswerText(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-sky-300 outline-none resize-none" />
          </div>
          <button data-testid="grade-btn" onClick={submit} disabled={loading}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3.5 rounded-full transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <><Loader2 size={20} className="animate-spin" /> AI проверява теста...</> : <><ScanLine size={20} /> Провери и оцени</>}
          </button>
        </div>

        {result && (
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="mt-8">
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">Резултат</h2>
            <ResourceContent resource={result} />
          </motion.div>
        )}
      </div>
    </Layout>
  );
}
